import { FC, JSX, useEffect, useState } from 'react';

import {
	copyTextToClipboard,
	EvButton,
	EvFlexColumn,
	EvFlexRow,
	EvLink,
	EvTextInput
} from '@evinced-private/ui-common';

import { getErrorMessage } from '../../helpers/ApiErrorHelper';
import { IServerError } from '../../services/server/ServerError';
import { getInvitation } from '../../services/UserInvitationService';
import { ProductHubPopup } from '../common/product-hub-popup/ProductHubPopup';

import { USER_INVITATION_POPUP_LABELS } from './UserInvitationPopup.labels';

import './UserInvitationPopup.scss';

interface IUserInvitationPopupProps {
	isOpen: boolean;
	invitationId: string;
	onClose: () => void;
	tenantId: string;
}

export const UserInvitationPopup: FC<IUserInvitationPopupProps> = ({
	isOpen = false,
	invitationId,
	onClose,
	tenantId
}: IUserInvitationPopupProps) => {
	const [email, setEmail] = useState<string | null>(null);
	const [invitationLink, setInvitationLink] = useState<string | null>(null);
	const [serverError, setServerError] = useState<string | null>(null);
	const [isLoading, setLoading] = useState<boolean>(true);

	const onPopupOpened = async (): Promise<void> => {
		setLoading(true);

		try {
			const invitation = await getInvitation(tenantId, invitationId);
			setEmail(invitation.targetEmail);
			setInvitationLink(invitation.url);
		} catch (error) {
			const message = getErrorMessage(error as IServerError) || '';
			setServerError(message);
		} finally {
			setLoading(false);
		}
	};

	const renderPopupContent = (): JSX.Element => {
		if (serverError) {
			return <div className="errors-area">{serverError}</div>;
		}
		return (
			<div className="instructions">
				{USER_INVITATION_POPUP_LABELS.sentEmailPrefix}{' '}
				<EvLink linkText={email ?? ''} url={`mailto:${email ?? ''}`} openInNewTab={true} />{' '}
				{USER_INVITATION_POPUP_LABELS.sentEmailSuffix}
				<br />
				<br />
				{USER_INVITATION_POPUP_LABELS.invitationInstructions}
				<EvFlexRow gap="SMALL" margin={['MEDIUM', 'NONE', 'NONE', 'NONE']} align="START">
					<EvFlexColumn flex="1" width="auto">
						<EvTextInput value={invitationLink ?? ''} isDisabled onChange={() => undefined} />
					</EvFlexColumn>
					<EvButton
						disabled={!invitationLink}
						onClick={() =>
							copyTextToClipboard(invitationLink ?? '', USER_INVITATION_POPUP_LABELS.copyTarget)
						}
					>
						{USER_INVITATION_POPUP_LABELS.copyButton}
					</EvButton>
				</EvFlexRow>
			</div>
		);
	};

	// reset state when popup closes
	useEffect(() => {
		if (!isOpen) {
			setEmail(null);
			setInvitationLink(null);
			setServerError(null);
		}
	}, [isOpen]);

	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			<ProductHubPopup
				isControlled
				isOpen={isOpen}
				isCentered
				onClose={onClose}
				onOpen={onPopupOpened}
				title={USER_INVITATION_POPUP_LABELS.title}
				className="user-invitation-popup"
				isPopupLoading={isLoading}
			>
				<div className="user-invitation-popup-wrapper">
					<div className="user-invitation-popup-content">{renderPopupContent()}</div>
				</div>
			</ProductHubPopup>
		</>
	);
};
